import {useEffect, useMemo} from "react";
import {FileText, Image as ImageIcon, Upload} from "lucide-react";
import {useTranslation} from "../../../shared/i18n";

const optionValue = (option) => String(option?.id ?? "");

const MediaField = ({
    accept,
    currentUrl,
    file,
    image = false,
    label,
    name,
    pending,
    required,
    onChange,
}) => {
    const {t} = useTranslation();
    const previewUrl = useMemo(
        () => image && file && typeof URL.createObjectURL === "function" ? URL.createObjectURL(file) : null,
        [file, image],
    );

    useEffect(() => () => {
        if (previewUrl && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    const visibleUrl = previewUrl || currentUrl;

    return (
        <div className="organization-media-field">
            <label className="organization-file-input">
                <span>{label}{required ? " *" : ""}</span>
                <span className="organization-file-control">
                    <Upload aria-hidden="true" size={16} />
                    {file?.name || t("orgs.common.chooseFile")}
                </span>
                <input
                    type="file"
                    name={name}
                    aria-label={label}
                    accept={accept}
                    disabled={pending}
                    onChange={onChange}
                />
            </label>
            {visibleUrl ? (
                image ? (
                    <a href={visibleUrl} target="_blank" rel="noreferrer" className="organization-media-preview">
                        <img src={visibleUrl} alt={t("orgs.common.preview", {label})} />
                    </a>
                ) : (
                    <a href={visibleUrl} target="_blank" rel="noreferrer" className="organization-document-preview">
                        <FileText aria-hidden="true" size={18} />
                        {file?.name || t("orgs.common.openCurrentDocument")}
                    </a>
                )
            ) : (
                <span className="organization-media-empty">
                    {image ? <ImageIcon aria-hidden="true" size={18} /> : <FileText aria-hidden="true" size={18} />}
                    {t("orgs.common.fileNotChosen")}
                </span>
            )}
        </div>
    );
};

const Field = ({label, name, value, pending, required = false, type = "text", onChange, ...props}) => (
    <label className="organization-field">
        <span>{label}{required ? " *" : ""}</span>
        <input
            {...props}
            aria-label={label}
            type={type}
            name={name}
            value={value}
            required={required}
            disabled={pending}
            onChange={onChange}
        />
    </label>
);

const SelectField = ({children, label, name, value, pending, required = false, onChange}) => (
    <label className="organization-field">
        <span>{label}{required ? " *" : ""}</span>
        <select
            aria-label={label}
            name={name}
            value={value}
            required={required}
            disabled={pending}
            onChange={onChange}
        >
            {children}
        </select>
    </label>
);

export const PlaceForm = ({
    values,
    existingMedia,
    cities,
    categories,
    deployers,
    isCreate,
    canAssignDeployer,
    pending,
    validationError,
    submitLabel,
    onChange,
    onFileChange,
    onSubmit,
}) => {
    const {t} = useTranslation();
    return (
    <form className="organization-form" onSubmit={onSubmit} noValidate>
        {validationError ? <div className="organization-form-error" role="alert">{validationError}</div> : null}

        <section className="organization-form-section" aria-labelledby="place-profile-heading">
            <header>
                <h2 id="place-profile-heading">{t("orgs.placeForm.profileTitle")}</h2>
                <p>{t("orgs.placeForm.profileCopy")}</p>
            </header>
            <div className="organization-form-grid">
                <Field label={t("orgs.placeForm.name")} name="name" value={values.name} pending={pending} required onChange={onChange} />
                <Field label={t("orgs.placeForm.head")} name="head_name" value={values.head_name} pending={pending} required onChange={onChange} />
                <Field label={t("orgs.common.phone")} name="phone_number" value={values.phone_number} pending={pending} required onChange={onChange} />
                <Field label={t("orgs.common.login")} name="login" value={values.login} pending={pending} required autoComplete="username" onChange={onChange} />
                {isCreate ? (
                    <Field
                        label={t("orgs.common.password")}
                        name="password"
                        value={values.password}
                        pending={pending}
                        required
                        type="password"
                        autoComplete="new-password"
                        onChange={onChange}
                    />
                ) : null}
                <SelectField label={t("orgs.common.city")} name="city" value={values.city} pending={pending} required onChange={onChange}>
                    <option value="">{t("orgs.common.chooseCity")}</option>
                    {cities.filter((city) => optionValue(city) !== "0").map((city) => (
                        <option key={optionValue(city)} value={optionValue(city)}>{city.name}</option>
                    ))}
                </SelectField>
                <SelectField label={t("orgs.common.category")} name="category" value={values.category} pending={pending} required onChange={onChange}>
                    <option value="">{t("orgs.common.chooseCategory")}</option>
                    {categories.filter((category) => optionValue(category) !== "0").map((category) => (
                        <option key={optionValue(category)} value={optionValue(category)}>{category.name}</option>
                    ))}
                </SelectField>
                {canAssignDeployer ? (
                    <SelectField label={t("orgs.placeForm.deployer")} name="created_by" value={values.created_by} pending={pending} onChange={onChange}>
                        <option value="">{t("orgs.placeForm.deployerEmpty")}</option>
                        {deployers.map((deployer) => (
                            <option key={optionValue(deployer)} value={optionValue(deployer)}>{deployer.name}</option>
                        ))}
                    </SelectField>
                ) : null}
            </div>
        </section>

        <section className="organization-form-section" aria-labelledby="place-legal-heading">
            <header>
                <h2 id="place-legal-heading">{t("orgs.placeForm.legalTitle")}</h2>
                <p>{t("orgs.placeForm.legalCopy")}</p>
            </header>
            <div className="organization-form-grid">
                <Field label={t("orgs.common.bin")} name="bin_number" value={values.bin_number} pending={pending} required onChange={onChange} />
                <Field label={t("orgs.common.account")} name="account_number" value={values.account_number} pending={pending} required onChange={onChange} />
                <Field
                    label={t("orgs.placeForm.percent")}
                    name="off_percent"
                    value={values.off_percent}
                    pending={pending}
                    required
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    onChange={onChange}
                />
            </div>
            <div className="organization-toggle-row">
                <label>
                    <input type="checkbox" name="is_shown" checked={values.is_shown} disabled={pending} onChange={onChange} />
                    <span>{t("orgs.common.showInApp")}</span>
                </label>
                <label>
                    <input type="checkbox" name="bonus_only" checked={values.bonus_only} disabled={pending} onChange={onChange} />
                    <span>{t("orgs.placeForm.bonusOnly")}</span>
                </label>
                <label>
                    <input
                        type="checkbox"
                        name="logo_missing"
                        checked={!values.logo_exists}
                        disabled={pending}
                        onChange={onChange}
                    />
                    <span>{t("orgs.placeForm.logoMissing")}</span>
                </label>
            </div>
        </section>

        <section className="organization-form-section" aria-labelledby="place-media-heading">
            <header>
                <h2 id="place-media-heading">{t("orgs.placeForm.mediaTitle")}</h2>
                <p>{t("orgs.placeForm.mediaCopy")}</p>
            </header>
            <div className="organization-media-grid">
                <MediaField
                    label={t("orgs.placeForm.logo")}
                    name="logo"
                    image
                    accept="image/*"
                    file={values.logo}
                    currentUrl={values.logo_exists ? existingMedia.logo : null}
                    pending={pending}
                    onChange={onFileChange}
                />
                <MediaField
                    label={t("orgs.placeForm.background")}
                    name="background"
                    image
                    accept="image/*"
                    file={values.background}
                    currentUrl={existingMedia.background}
                    pending={pending}
                    required={isCreate}
                    onChange={onFileChange}
                />
                <MediaField
                    label={t("orgs.placeForm.agreement")}
                    name="agreement"
                    accept=".pdf,image/*"
                    file={values.agreement}
                    currentUrl={existingMedia.agreement}
                    pending={pending}
                    required={isCreate}
                    onChange={onFileChange}
                />
            </div>
        </section>

        <footer className="organization-form-actions">
            <button className="admin-button admin-button-primary" type="submit" disabled={pending}>
                {pending ? t("orgs.common.saving") : submitLabel}
            </button>
        </footer>
    </form>
    );
};
